// Reach is a display of the compiled ledger. Size shares its cold definition read,
// then keeps the existing transition renderer and active-panel/drillback lifecycle.
import { catalogShell, itemsCol, layerOf, sizeIdOf } from '../shared/array-types';
import { parseText } from '../shared/parse-text';
import { renderSourceLineBlock, resolveLineKind, resolveLineLabel } from './source-line-block';
import { setActiveInfoPanel, paintActiveInfoPanel } from './active-panel';
import { renderSizeInfoPanel as renderNativeSizeInfoPanel } from './size-info';
import type { CachedCaster, SourceRow } from './types';

const REACH_ID = '36D59FF4-6778-96ED-8664-F5D63638C77B';
type Row = Record<string, unknown>;
const record = (value: unknown): Row => value && typeof value === 'object' && !Array.isArray(value) ? value as Row : {};
const cachedCaster = (id: string): CachedCaster | undefined => window.FateTide?.casterCache?.get(id) as CachedCaster | undefined;

function reachSources(caster: CachedCaster): SourceRow[] {
    const sources = record(caster.computed?.creature?.reach).sources;
    return Array.isArray(sources) ? sources : [];
}

async function readCaster(casterId: string, targetId: string): Promise<CachedCaster | undefined> {
    const target = document.getElementById(targetId);
    if (!target) return;
    if (!cachedCaster(casterId)) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try { await fetchAndCacheCaster(casterId); } catch { /* cache miss is displayed below */ }
    }
    const caster = cachedCaster(casterId);
    if (!caster) target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
    return caster;
}

/** Use the websocket bundle's page store via its bridge; never instantiate another store. */
async function hydrateDisplayDefinitions(caster: CachedCaster, termId: string, sourceIds: string[]): Promise<void> {
    const missing = [...new Set([termId, ...sourceIds])].filter(id => {
        if (!checkIfValidUUID(id)) return false;
        const shell = catalogShell(caster, id);
        return id === termId
            ? !itemsCol(shell, 'item_name') || itemsCol(shell, 'item_description') === undefined
            : !resolveLineLabel(caster, id);
    });
    if (!missing.length || !window.ftDefinitionsForDisplay) return;
    const definitions = await window.ftDefinitionsForDisplay(missing);
    const catalog = caster.catalog ??= {};
    for (const [id, definition] of Object.entries(definitions)) {
        if (definition) catalog[id] = definition;
    }
}

function renderReach(caster: CachedCaster, heldTerm: unknown): string {
    // The page reader bridges scalar/{total} slots and authored layers. Lean zero
    // clusters are omitted; do not sum sources or rerun reach arithmetic here.
    const total = Number(ftLayerValue(caster, 'reach') ?? 0);
    const term = catalogShell(caster, REACH_ID);
    const name = String(itemsCol(term, 'item_name') || itemsCol(heldTerm, 'item_name') || 'Reach');
    const description = String(itemsCol(term, 'item_description') ?? itemsCol(heldTerm, 'item_description') ?? '');
    const sources = reachSources(caster).map(source => ({
        ...source,
        label: source.label || resolveLineLabel(caster, source.href_id ?? ''),
        kind: resolveLineKind(caster, source) || source.category || 'other',
    }));
    const raw = layerOf(caster, 'reach');
    const braced = total !== 0 && (raw === `{${total}}`
        || sources.some(source => source.kind !== 'remainder' && source.value_brace === total));
    const breakdown = renderSourceLineBlock(sources, 'reach', total, 'bg-secondary', caster);
    // The shared block owns source rows and drillback; only its footer badge gets
    // the stored override notation, matching the contributing brace without a roll.
    const footer = braced ? breakdown.replace(`<span class="badge bg-secondary">${total}</span>`, `<span class="badge bg-secondary">{${total}}</span>`) : breakdown;
    return `<table class="table border">
        <thead><tr><th scope="col" colspan="2" class="table-dark align-middle"><span role="button" onclick="openItemInfo('${REACH_ID}')">${iconMap(REACH_ID)} ${escapeHtml(name)}</span></th></tr></thead>
        <tbody><tr><th scope="row" class="align-middle">Current</th><td class="text-end"><h3 class="mb-0"><span class="badge bg-primary">${total}</span></h3></td></tr></tbody>
    </table>
    ${description ? `<div class="text-start border rounded p-3 mb-3 small">${parseText(description)}</div>` : ''}
    <div class="mb-2"><div class="small text-muted mb-1">Sources</div>${footer}</div>`;
}

export async function renderReachInfoPanel(casterId: string, targetId = 'div_refresh_side_info'): Promise<void> {
    const caster = await readCaster(casterId, targetId);
    if (!caster) return;
    await hydrateDisplayDefinitions(caster, REACH_ID, reachSources(caster).map(source => source.href_id ?? ''));
    const term = catalogShell(caster, REACH_ID);
    setActiveInfoPanel(casterId, targetId, current => renderReach(current, term));
    paintActiveInfoPanel(cachedCaster(casterId) ?? caster);
}

export async function renderSizeInfoPanel(casterId: string, targetId = 'div_refresh_side_info'): Promise<void> {
    const caster = await readCaster(casterId, targetId);
    if (!caster) return;
    const breakdown = record(caster.computed?.creature?.size?.breakdown);
    const base = record(breakdown.base);
    const events = Array.isArray(breakdown.events) ? breakdown.events.map(record) : [];
    await hydrateDisplayDefinitions(caster, sizeIdOf(caster) ?? '', [base, ...events].map(row => String(row.source_id ?? '')));
    await renderNativeSizeInfoPanel(casterId, targetId);
}

declare global {
    interface Window {
        ftDefinitionsForDisplay?: (ids: string[]) => Promise<Record<string, Row | null>>;
    }
}
